import { browserSupportsWebAuthn, startRegistration, type PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { formatDate, formatTime } from '@trellistry/platform/i18n';
import { Actions, Button, Card, Loading, Notice, PageHeader, TextField } from '@trellistry/ui';
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, account, api, type AccountSummary } from '../api.js';
import { CodeList } from './NewDetails.js';

/**
 * My sign-in (design §5): a person's passkeys, remembered devices and backup codes. An administrator also finds here
 * the way to help someone sign in again, which gives a code to pass on. Nothing here shows a secret after the moment
 * it is made.
 */
const when = (iso: string) => formatDate(new Date(iso));
const jsonPost = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });

/** A passkey or a device: what it is, when, and a way to take it back. */
function SignInItems<T extends { id: string }>({ items, describe, empty }: { items: T[]; describe: (item: T) => { name: string; detail: string; action: ReactNode }; empty: string }) {
  if (items.length === 0) return <p className="muted">{empty}</p>;
  return (
    <ul className="items">
      {items.map((item) => {
        const { name, detail, action } = describe(item);
        return <li key={item.id}><span>{name}<small>{detail}</small></span>{action}</li>;
      })}
    </ul>
  );
}

export function Account({ mayHelpOthers }: { mayHelpOthers: boolean }) {
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [passkeyName, setPasskeyName] = useState('');
  const [busy, setBusy] = useState(false);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);
  const [confirmingCodes, setConfirmingCodes] = useState(false);

  const load = useCallback(async () => {
    try { setSummary(await account<AccountSummary>('')); } catch { setError('We couldn’t load your details just now. Please try again.'); }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const addPasskey = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const begun = await account<{ status: 'held' } | { status: 'ok'; challengeId: string; options: PublicKeyCredentialCreationOptionsJSON }>('/passkey/begin', jsonPost({}));
      if (begun.status === 'held') { setError('Passkeys will be ready here once your church’s own web address is active.'); return; }
      const response = await startRegistration({ optionsJSON: begun.options });
      await account('/passkey/complete', jsonPost({ challengeId: begun.challengeId, response, name: passkeyName }));
      setPasskeyName('');
      await load();
    } catch (e) {
      // closing the browser's passkey window is a change of mind, not an error
      if ((e as Error).name !== 'NotAllowedError') setError('We couldn’t add that passkey. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (path: string) => {
    setError(null);
    try { await account(path, { method: 'DELETE' }); await load(); } catch { setError('We couldn’t make that change. Please try again.'); }
  };

  const makeCodes = async () => {
    setError(null);
    try {
      const r = await account<{ backupCodes: string[] }>('/backup-codes', jsonPost({}));
      setNewCodes(r.backupCodes);
      setConfirmingCodes(false);
      await load();
    } catch { setError('We couldn’t make new codes. Please try again.'); }
  };

  return (
    <div className="reading-width">
      <PageHeader title="My sign-in" />
      {error && <Notice tone="error">{error}</Notice>}
      {!summary ? !error && <Loading>One moment…</Loading> : (
        <>
          <Card title="Passkeys">
            <p className="muted">A passkey signs you in with your fingerprint, face or device PIN, with no password or code.</p>
            <SignInItems items={summary.passkeys} empty="You haven’t added one yet." describe={(p) => ({
              name: p.name,
              detail: `Added ${when(p.createdAt)}${p.lastUsedAt ? `, last used ${when(p.lastUsedAt)}` : ''}`,
              action: <Button variant="quiet" onClick={() => remove(`/passkey/${p.id}`)}>Remove</Button>,
            })} />
            {browserSupportsWebAuthn() ? (
              <form onSubmit={addPasskey}>
                <TextField label="A name to recognise it by" placeholder="My phone" value={passkeyName} onChange={(e) => setPasskeyName(e.target.value)} maxLength={60} />
                <Actions><Button type="submit" busy={busy} busyLabel="Waiting for your device…">Add a passkey</Button></Actions>
              </form>
            ) : <p className="muted">This browser can’t use passkeys.</p>}
          </Card>

          <Card title="Remembered devices">
            <p className="muted">These devices skip the code from your authenticator app for 30 days.</p>
            <SignInItems items={summary.devices} empty="No devices are remembered." describe={(d) => ({
              name: d.label || 'A device',
              detail: `Remembered ${when(d.rememberedAt)}, until ${when(d.expiresAt)}`,
              action: <Button variant="quiet" onClick={() => remove(`/device/${d.id}`)}>Forget</Button>,
            })} />
          </Card>

          <Card title="Backup codes">
            <p className="muted">You have {summary.backupCodesLeft} left. Each lets you in once if you lose your phone.</p>
            {newCodes && (
              <>
                <Notice>Here are your new codes. We’ll show them only now, and your old codes no longer work.</Notice>
                <CodeList codes={newCodes} />
                <Button variant="quiet" onClick={() => setNewCodes(null)}>I’ve kept them somewhere safe</Button>
              </>
            )}
            {!newCodes && (confirmingCodes ? (
              <div className="row">
                <span>Your old codes will stop working.</span>
                <Button onClick={makeCodes}>Make new codes</Button>
                <Button variant="quiet" onClick={() => setConfirmingCodes(false)}>Keep my codes</Button>
              </div>
            ) : <Button variant="quiet" onClick={() => setConfirmingCodes(true)}>Make new codes</Button>)}
          </Card>

          {mayHelpOthers && <HelpSomeone />}
        </>
      )}
    </div>
  );
}

/** Lets a staff member recover without a backup code: the administrator passes on the code this gives. */
function HelpSomeone() {
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [result, setResult] = useState<{ approvalId: string; displayName: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      setResult(await api('/recovery-approvals', jsonPost({ email, note })));
      setEmail('');
      setNote('');
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      setError(status === 404 ? 'We couldn’t find a staff account with that email address.'
        : status === 409 ? 'You can’t approve this for yourself. Please ask another administrator.'
        : 'We couldn’t do that just now. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card title="Help someone sign in again">
      <p className="muted">When a staff member has lost their phone and their backup codes, first make sure it’s really them, by a call or face to face. Then pass them the code this gives. It works once, within an hour, together with a code we email them.</p>
      {error && <Notice tone="error">{error}</Notice>}
      {result && (
        <Notice>
          The code for {result.displayName}, until {formatTime(new Date(result.expiresAt))}:
          <p className="secret">{result.approvalId}</p>
        </Notice>
      )}
      <form onSubmit={submit}>
        <TextField label="Their email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <TextField label="How you checked it’s them (optional)" maxLength={200} value={note} onChange={(e) => setNote(e.target.value)} />
        <Actions><Button type="submit" busy={busy} busyLabel="Working…">Give me a code</Button></Actions>
      </form>
    </Card>
  );
}
